import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const ACTION_LABEL = {
  hold: "Continue Holding",
  no_action: "No Action Needed",
  move_stop_breakeven: "Move Stop to Breakeven",
  tighten_stop: "Tighten Stop",
  widen_stop: "Widen Stop",
  raise_target: "Raise Profit Target",
  lower_target: "Lower Profit Target",
  activate_trailing_stop: "Activate Trailing Stop",
  take_partial_profit: "Take Partial Profit",
  exit_immediately: "Exit Position Immediately",
};
const THESIS_COLOR = {
  valid: "pnl-up border-current",
  weakening: "text-signal-yellow border-current",
  invalidated: "pnl-down border-current",
};
const URGENCY_COLOR = {
  low: "text-muted-foreground border-current",
  medium: "text-signal-yellow border-current",
  high: "pnl-down border-current",
};

export default function PositionReviewDialog({ open, onClose, position, onApplied }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | ready | applying
  const [review, setReview] = useState(null); // { review_id, recommendation, position }

  const runReview = async () => {
    setPhase("loading");
    try {
      const { data } = await api.post(`/positions/${position.id}/review`);
      setReview(data);
      setPhase("ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Review failed");
      setPhase("idle");
    }
  };

  const apply = async () => {
    setPhase("applying");
    try {
      await api.post(`/positions/${position.id}/apply-recommendation`, { review_id: review.review_id });
      toast.success("Recommendation applied.");
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
      setPhase("ready");
    }
  };

  const rej = async () => {
    if (!review?.review_id) return onClose?.();
    try { await api.post(`/reviews/${review.review_id}/reject`); } catch { /* noop */ }
    toast.info("Recommendation dismissed.");
    onClose?.();
  };

  const rec = review?.recommendation || {};
  const pos = review?.position || position;

  return (
    <Dialog open={open} onOpenChange={(v)=>!v && onClose?.()}>
      <DialogContent className="max-w-3xl !rounded-none border-border/70" data-testid="position-review-dialog">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-wider text-sm">
            AI Position Review · {position?.symbol}
          </DialogTitle>
        </DialogHeader>

        {phase === "idle" && (
          <div className="p-8 text-center space-y-4">
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Retrieve fresh market data, re-analyze the original thesis, and receive a structured recommendation for this open position.
              Nothing will change automatically — you approve every action.
            </p>
            <button className="btn-primary" onClick={runReview} data-testid="run-position-review-btn">
              Run AI Review
            </button>
          </div>
        )}

        {phase === "loading" && (
          <div className="p-10 text-center flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="animate-spin" size={16}/> Analyzing position…
          </div>
        )}

        {(phase === "ready" || phase === "applying") && review && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-px bg-border/40">
              <div className="p-2 bg-card/40"><div className="label-cap">Current</div><div className="mono text-sm">${pos.current_price?.toFixed?.(2)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Entry</div><div className="mono text-sm">${pos.entry_price?.toFixed?.(2)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Stop</div><div className="mono text-sm pnl-down">${pos.stop_price?.toFixed?.(2)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Target</div><div className="mono text-sm pnl-up">${pos.target_price?.toFixed?.(2)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Qty</div><div className="mono text-sm">{pos.qty}</div></div>
              <div className="p-2 bg-card/40 col-span-2">
                <div className="label-cap">P&L</div>
                <div className={`mono text-sm ${(pos.unrealized_pnl ?? 0) >= 0 ? "pnl-up" : "pnl-down"}`}>
                  {(pos.unrealized_pnl ?? 0) >= 0 ? "+" : ""}${pos.unrealized_pnl?.toFixed?.(2)} ({pos.unrealized_pnl_pct?.toFixed?.(2)}%)
                </div>
              </div>
              <div className="p-2 bg-card/40"><div className="label-cap">Confidence</div><div className="mono text-sm">{rec.confidence ?? 0}%</div></div>
            </div>

            <div className="border border-border/60 p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="label-cap">Recommendation</div>
                <span className="chip !text-[11px]" data-testid="review-action-label">{ACTION_LABEL[rec.action] || rec.action}</span>
                <span className={`chip !text-[10px] ${THESIS_COLOR[rec.thesis_status] || ""}`}>Thesis {rec.thesis_status}</span>
                <span className={`chip !text-[10px] ${URGENCY_COLOR[rec.urgency] || ""}`}>{(rec.urgency || "").toUpperCase()} URGENCY</span>
                {rec._source === "fallback" && <span className="chip pnl-down border-current !text-[10px]"><AlertTriangle size={10}/> AI unavailable</span>}
              </div>
              <p className="text-sm">{rec.reasoning}</p>
              <div className="grid grid-cols-3 gap-px bg-border/40 text-xs">
                <div className="p-2 bg-card/40"><div className="label-cap">Proposed Stop</div><div className="mono text-sm">{rec.proposed_stop ? `$${rec.proposed_stop.toFixed?.(2) ?? rec.proposed_stop}` : "—"}</div></div>
                <div className="p-2 bg-card/40"><div className="label-cap">Proposed Target</div><div className="mono text-sm">{rec.proposed_target ? `$${rec.proposed_target.toFixed?.(2) ?? rec.proposed_target}` : "—"}</div></div>
                <div className="p-2 bg-card/40"><div className="label-cap">Updated R/R</div><div className="mono text-sm">{rec.updated_reward_risk || "—"}</div></div>
                {rec.action === "take_partial_profit" && (
                  <>
                    <div className="p-2 bg-card/40 col-span-3"><div className="label-cap">Partial Take</div><div className="mono text-sm">{rec.proposed_partial_pct}% of position</div></div>
                  </>
                )}
                {rec.action === "activate_trailing_stop" && (
                  <div className="p-2 bg-card/40 col-span-3"><div className="label-cap">Trailing Stop</div><div className="mono text-sm">{rec.trail_stop_type} · {rec.trail_stop_value}</div></div>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button className="btn-outline" onClick={rej} data-testid="review-reject-btn">
                <XCircle size={14}/> Reject
              </button>
              <button
                className="btn-primary"
                onClick={apply}
                disabled={phase === "applying" || ["hold", "no_action"].includes(rec.action)}
                data-testid="review-apply-btn"
              >
                {phase === "applying" ? <Loader2 className="animate-spin" size={14}/> : <CheckCircle2 size={14}/>}
                {["hold", "no_action"].includes(rec.action) ? "No Action to Apply" : "Approve & Apply"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
